import { FC, ReactNode, useCallback, useState } from 'react'
import { AppraisalStepper } from '../../../../components/custom_components/Appraisal/AppraisalStepper/AppraisalStepper'
import { ContentContainer } from '../../../../components/custom_components/ContentContainer'
import {
  FullMeetingWithEvaluationAndSelfReflectionFragment,
  MeetingStep,
  MeetingStepStatus,
  MeetingStepType,
  MeetingStepViewId,
} from '../../../../types'
import { AppraisalFlowProvider } from '../shared/AppraisalFlowProvider'
import { setMeetingStep } from '../shared/AppraisalMeetingService'
import { AppraisalBackBtn, AppraisalNextBtn } from '../shared/MeetingStepButton'
import AppraisalMeetingStepConclusion from './AppraisalMeetingStepConclusion'
import AppraisalMeetingStepContractChanges from './AppraisalMeetingStepContractChanges'
import AppraisalMeetingStepEvaluation from './AppraisalMeetingStepEvaluation'
import AppraisalMeetingStepMeetingNotes from './AppraisalMeetingStepMeetingNotes'
import AppraisalMeetingStepNewGoals from './AppraisalMeetingStepNewTargets'
import AppraisalMeetingStepOverview from './AppraisalMeetingStepOverview'
import { AppraisalMeetingStepPastTargets } from './AppraisalMeetingStepPastTargets'
import { AppraisalMeetingStepPayments } from './AppraisalMeetingStepPayments'
import AppraisalMeetingStepSelfReflection from './AppraisalMeetingStepSelfReflection'
import { useCollapse } from '../../../../routes/collapse-context'
import useCountrySettings from '../../../../graphql/countrySettings/hooks/useCountrySettings'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'

interface Props {
  meeting: FullMeetingWithEvaluationAndSelfReflectionFragment
}

const AppraisalMeetingView: FC<Props> = ({ meeting }) => {
  const [animate, setAnimate] = useState(false)
  const [processing, setProcessing] = useState(false)

  const collapsed = useCollapse()

  const updateMeetingStep = useCallback(
    (step: MeetingStep, currentStepStatus?: MeetingStepStatus, callback?: () => void) => {
      setAnimate(true)
      setProcessing(true)
      if (callback) callback()
      setMeetingStep(meeting.id, step, currentStepStatus).then(() => {
        setProcessing(false)
      })
    },
    [meeting.id],
  )

  const { data, error, loading } = useCountrySettings(meeting.employee.currentContract!.legalEntity.country)

  if (loading || error || !data || !data.getCountrySettings) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.getCountrySettings} error={error} />
  }

  const steps: MeetingStep[] = meeting.meetingSteps
    ? meeting.meetingSteps.filter((step) => step.type === MeetingStepType.Meeting).sort((a, b) => a.position - b.position)
    : []

  const activeStep = steps.find((x) => meeting.activeMeetingStep && x.id === meeting.activeMeetingStep.id)
  const activeStepIndex = activeStep ? steps.indexOf(activeStep) : -1

  // todo: create a more robust version
  const nextStep = steps[activeStepIndex + 1]
  const previousStep = steps[activeStepIndex - 1]

  const BackBtn = (currentStepStatus?: MeetingStepStatus) => {
    return <AppraisalBackBtn nextStep={previousStep} onClick={() => updateMeetingStep(previousStep, currentStepStatus)} />
  }
  const NextBtn = (currentStepStatus?: MeetingStepStatus, disabled?: boolean, tooltip?: string | ReactNode, callback?: () => void) => {
    const eventHandler = {
      onMouseDown: () => updateMeetingStep(nextStep, currentStepStatus, callback),
    }
    return (
      <AppraisalNextBtn data-e2e-next-step-button nextStep={nextStep} disabled={disabled} tooltip={tooltip} eventHandler={eventHandler} />
    )
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <AppraisalFlowProvider meeting={meeting} steps={steps}>
        <AppraisalStepper
          meetingType={meeting.type}
          employee={meeting.employee}
          steps={steps}
          activeStepIndex={activeStepIndex}
          animate={animate}
          processing={processing}
          onStepClick={updateMeetingStep}
        />
        <div data-e2e-appraisal-meeting-container>
          {activeStep && activeStep.viewId === MeetingStepViewId.Intro && (
            <AppraisalMeetingStepOverview meeting={meeting} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.SelfReflection && (
            <AppraisalMeetingStepSelfReflection meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.Evaluation && (
            <AppraisalMeetingStepEvaluation meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.PastTargets && (
            <AppraisalMeetingStepPastTargets meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.Payments && (
            <AppraisalMeetingStepPayments meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.ContractChanges && (
            <AppraisalMeetingStepContractChanges
              meeting={meeting}
              countrySettings={data.getCountrySettings}
              backBtn={BackBtn}
              nextBtn={NextBtn}
            />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.NewTargets && (
            <AppraisalMeetingStepNewGoals meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.MeetingNotes && (
            <AppraisalMeetingStepMeetingNotes meeting={meeting} backBtn={BackBtn} nextBtn={NextBtn} />
          )}
          {activeStep && activeStep.viewId === MeetingStepViewId.Conclusion && (
            <AppraisalMeetingStepConclusion meeting={meeting} countrySettings={data.getCountrySettings} backBtn={BackBtn} />
          )}
        </div>
      </AppraisalFlowProvider>
    </ContentContainer>
  )
}

export default AppraisalMeetingView
